import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Alert, Badge, Button, HelpTip } from '@/components/ui';
import { api } from '@/lib/api';
import { formatDataHoraSegundos, formatRelativeDate } from '@/lib/format-date';

/**
 * 🔴 A CONEXÃO DO WHATSAPP (09/10/2026, mig 713) — o pedido do Matheus, literal:
 * *"como eu acesso e reconecto o evolution? eu deveria conseguir ler o QR code de reconexão pelo v2"*.
 *
 * Em 09/10 a sessão caiu ~12h e ele dependeu de uma sessão do Claude Code para levantar. Esta seção mostra o estado
 * (perguntado à Evolution pelo servidor — a chave nunca chega aqui), o último envio que saiu, as falhas seguidas, o que
 * está esperando para sair quando voltar (para não reconectar às cegas) e, sob demanda, o QR.
 *
 * ⚠️ Fica ACIMA das abas, como o freio: quando o bot cai, é a primeira coisa que se procura — e ele vai usá-la com o
 * celular do bot na mão, então ela precisa funcionar em 375 px.
 * ❌ Reiniciar a instância é escrita no caminho de envio e NÃO está aqui (decisão pendente do Matheus).
 */

interface Painel {
  estado: string | null;
  estadoEmPortugues: string;
  desdeIso: string | null;
  sondadaEmIso: string | null;
  ultimoEnvioOkIso: string | null;
  falhasSeguidas: number;
  gruposNasFalhas: number;
  alarmeEmIso: string | null;
  alarmeMotivo: string | null;
  pendencias: { envio: number; esgotadas: number; relato: number; ia: number };
}

const AJUDA_CONEXAO =
  'Se o número do bot está conectado ao WhatsApp. O sistema confere sozinho a cada 5 minutos e, se a conexão ficar '
  + 'fora por 10 minutos, os administradores recebem um e-mail. Desconectado, nada sai: as mensagens de OS ficam '
  + 'esperando e saem uma vez só quando voltar. Para reconectar, gere o QR e leia com o celular do bot.';

/**
 * ⚠️ Esta seção mora no mesmo cartão do FREIO de emergência: uma resposta fora do formato não pode derrubar o cartão.
 * Formato inesperado = a seção some calada; o resto do cartão segue.
 */
function ehPainel(x: unknown): x is Painel {
  const p = x as Partial<Painel> | null;
  return Boolean(p && typeof p === 'object' && typeof p.estadoEmPortugues === 'string' && p.pendencias && typeof p.pendencias === 'object');
}

const VARIANTE: Record<string, 'success' | 'warning' | 'danger' | 'neutral'> = {
  open: 'success',
  connecting: 'warning',
  close: 'danger',
  sem_resposta: 'danger',
  sem_credencial: 'neutral',
};

export function ConexaoDoBot() {
  const [p, setP] = useState<Painel | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [gerando, setGerando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get<unknown>('/api/v1/admin/whatsapp/conexao');
      if (ehPainel(r)) setP(r);
    } catch {
      /* a seção some calada se a rota falhar — o resto do cartão segue */
    }
  }, []);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  // Com o QR na tela, confere a cada 5 s: assim que ler, a tela mostra "Conectado" e tira o QR.
  useEffect(() => {
    if (!qr) return undefined;
    const t = setInterval(() => {
      void api
        .post<Painel>('/api/v1/admin/whatsapp/conexao/conferir')
        .then((novo) => {
          if (!ehPainel(novo)) return;
          setP(novo);
          if (novo.estado === 'open') setQr(null);
        })
        .catch(() => undefined);
    }, 5000);
    return () => clearInterval(t);
  }, [qr]);

  async function conferir() {
    setConferindo(true);
    setErro(null);
    try {
      const r = await api.post<unknown>('/api/v1/admin/whatsapp/conexao/conferir');
      if (ehPainel(r)) setP(r);
    } catch {
      setErro('Não deu para conferir agora. Tente de novo em instantes.');
    } finally {
      setConferindo(false);
    }
  }

  async function gerarQr() {
    setGerando(true);
    setErro(null);
    try {
      const r = await api.post<{ qrDataUrl: string }>('/api/v1/admin/whatsapp/conexao/qr');
      setQr(r.qrDataUrl);
    } catch (err) {
      setQr(null);
      setErro(err instanceof Error ? err.message : 'Não deu para gerar o QR.');
    } finally {
      setGerando(false);
    }
  }

  if (!p) return null;
  const conectado = p.estado === 'open';
  const esperando = p.pendencias.envio + p.pendencias.esgotadas;

  return (
    <section
      className="space-y-3 rounded-lg border border-line p-3 sm:p-4 dark:border-line-dark"
      aria-labelledby="wa-conexao-titulo"
      data-testid="wa-conexao"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <h3 id="wa-conexao-titulo" className="text-sm font-medium text-ink-1 dark:text-ink-dark-1">
            Conexão do WhatsApp
          </h3>
          <HelpTip texto={AJUDA_CONEXAO} />
        </div>
        <Badge variant={VARIANTE[p.estado ?? ''] ?? 'neutral'} size="sm" data-testid="wa-conexao-estado">
          {p.estadoEmPortugues}
        </Badge>
      </div>

      <dl className="grid grid-cols-1 gap-x-4 gap-y-1 text-xs sm:grid-cols-2">
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-ink-3 dark:text-ink-dark-3">Neste estado desde</dt>
          <dd className="text-ink-1 dark:text-ink-dark-1">{p.desdeIso ? formatDataHoraSegundos(p.desdeIso) : '—'}</dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-ink-3 dark:text-ink-dark-3">Última mensagem que saiu</dt>
          <dd className="text-ink-1 dark:text-ink-dark-1">
            {p.ultimoEnvioOkIso ? formatRelativeDate(p.ultimoEnvioOkIso) : '—'}
          </dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-ink-3 dark:text-ink-dark-3">Falhas seguidas</dt>
          <dd className="text-ink-1 dark:text-ink-dark-1" data-testid="wa-conexao-falhas">
            {p.falhasSeguidas === 0 ? 'nenhuma' : `${p.falhasSeguidas} (em ${p.gruposNasFalhas} grupo${p.gruposNasFalhas === 1 ? '' : 's'})`}
          </dd>
        </div>
        <div className="flex justify-between gap-2 sm:block">
          <dt className="text-ink-3 dark:text-ink-dark-3">Esperando para sair</dt>
          <dd className="text-ink-1 dark:text-ink-dark-1" data-testid="wa-conexao-esperando">
            {esperando === 0 ? 'nada' : `${esperando} ${esperando === 1 ? 'mensagem' : 'mensagens'} de OS`}
            {p.pendencias.relato > 0 ? ` · ${p.pendencias.relato} esperando o relato do técnico` : ''}
          </dd>
        </div>
      </dl>

      {p.alarmeEmIso && (
        <Alert variant="danger">
          Aviso de queda enviado por e-mail em {formatDataHoraSegundos(p.alarmeEmIso)}. {p.alarmeMotivo}
        </Alert>
      )}
      {!conectado && esperando > 0 && (
        <p className="text-xs text-ink-2 dark:text-ink-dark-2">
          Ao reconectar, {esperando === 1 ? 'a mensagem que está esperando sai' : `as ${esperando} mensagens que estão esperando saem`}{' '}
          <strong>uma vez só</strong> em cada grupo.
          {p.pendencias.esgotadas > 0 ? ` ${p.pendencias.esgotadas} esgotaram as tentativas e ficam paradas na fila da IA.` : ''}
        </p>
      )}

      {erro && <Alert variant="warning">{erro}</Alert>}

      {qr && (
        <div className="flex flex-col items-center gap-2 rounded-md bg-white p-3" data-testid="wa-conexao-qr">
          <img src={qr} alt="QR para reconectar o WhatsApp do bot" className="h-auto w-full max-w-[264px]" />
          <p className="text-center text-xs text-ink-2">
            No <strong>celular do bot</strong>: WhatsApp › Aparelhos conectados › Conectar um aparelho. O QR expira em
            menos de um minuto.
          </p>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {!conectado && p.estado !== 'sem_credencial' && (
          <Button size="sm" onClick={() => void gerarQr()} loading={gerando} data-testid="wa-conexao-gerar-qr">
            {qr ? 'Gerar outro' : 'Gerar QR para reconectar'}
          </Button>
        )}
        <Button size="sm" variant="secondary" onClick={() => void conferir()} loading={conferindo}>
          Conferir agora
        </Button>
        <Link to="/ajuda?artigo=whatsapp-conexao-do-bot" className="inline-flex min-h-11 items-center px-1 text-xs text-red underline-offset-2 hover:underline sm:min-h-0">
          Como reconectar
        </Link>
      </div>
      {p.sondadaEmIso && (
        <p className="text-xs text-ink-3 dark:text-ink-dark-3">Conferido {formatRelativeDate(p.sondadaEmIso)}.</p>
      )}
    </section>
  );
}
